import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Save, Trash2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { api, errMsg, CAR_TYPES } from "@/lib/api";

const EMPTY = { name: "Nowy pakiet", description: "", included: [], prices: { sedan: 0, suv: 0, kombi: 0, van: 0 }, active: true, order: 99 };

const PackageEditor = ({ pkg, onSaved, onDeleted }) => {
  const [p, setP] = useState({ ...pkg, includedText: (pkg.included || []).join("\n") });
  const set = (k, v) => setP((s) => ({ ...s, [k]: v }));
  const tid = pkg.id || "new";

  const save = async () => {
    const body = { name: p.name, description: p.description, active: p.active, order: Number(p.order) || 0,
      included: p.includedText.split("\n").map((x) => x.trim()).filter(Boolean),
      prices: Object.fromEntries(CAR_TYPES.map(({ id }) => [id, Number(p.prices[id]) || 0])) };
    try {
      const { data } = pkg.id ? await api.put(`/admin/packages/${pkg.id}`, body) : await api.post("/admin/packages", body);
      toast.success("Zapisano pakiet"); onSaved(data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  const remove = async () => {
    if (!pkg.id) return onDeleted();
    if (!window.confirm(`Usunąć pakiet „${pkg.name}”?`)) return;
    await api.delete(`/admin/packages/${pkg.id}`); toast.success("Usunięto pakiet"); onDeleted();
  };

  return (
    <div data-testid={`package-editor-${tid}`} className="panel p-6 space-y-4">
      <div className="flex gap-3 items-center">
        <Input data-testid={`package-name-${tid}`} value={p.name} onChange={(e) => set("name", e.target.value)} className="bg-black/40 font-semibold" />
        <label className="flex items-center gap-2 text-xs shrink-0">Aktywny <Switch data-testid={`package-active-${tid}`} checked={p.active} onCheckedChange={(v) => set("active", v)} /></label>
      </div>
      <Input data-testid={`package-desc-${tid}`} placeholder="Opis" value={p.description} onChange={(e) => set("description", e.target.value)} className="bg-black/40" />
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {CAR_TYPES.map(({ id, label }) => (
          <label key={id} className="text-xs text-white/60">{label} (zł)
            <Input data-testid={`package-price-${tid}-${id}`} type="number" min="0" value={p.prices[id]} onChange={(e) => set("prices", { ...p.prices, [id]: e.target.value })} className="bg-black/40 mt-1" />
          </label>
        ))}
      </div>
      <label className="block text-xs text-white/60">Usługi w pakiecie (jedna na linię)
        <Textarea data-testid={`package-included-${tid}`} rows={3} value={p.includedText} onChange={(e) => set("includedText", e.target.value)} className="bg-black/40 mt-1" />
      </label>
      <div className="flex items-center justify-between gap-3">
        <label className="text-xs text-white/60 flex items-center gap-2">Kolejność <Input type="number" value={p.order} onChange={(e) => set("order", e.target.value)} className="bg-black/40 w-20 h-8" /></label>
        <div className="flex gap-2">
          <button data-testid={`package-delete-${tid}`} onClick={remove} className="p-2 rounded-md border border-white/15 text-red-400 hover:border-red-400"><Trash2 size={16} /></button>
          <button data-testid={`package-save-${tid}`} onClick={save} className="btn-y px-4 py-2 text-sm inline-flex items-center gap-2"><Save size={16} /> Zapisz</button>
        </div>
      </div>
    </div>
  );
};

export const PackagesTab = () => {
  const [list, setList] = useState(null);
  const [adding, setAdding] = useState(false);
  const load = () => api.get("/admin/packages").then((r) => setList(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-packages-tab">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-display text-xl sm:text-2xl font-bold">Pakiety i ceny</h2>
        <button data-testid="package-add-button" onClick={() => setAdding(true)} disabled={adding} className="btn-y px-4 py-2 text-sm inline-flex items-center gap-2"><Plus size={16} /> Dodaj pakiet</button>
      </div>
      <div className="grid xl:grid-cols-2 gap-5">
        {adding && <PackageEditor pkg={EMPTY} onSaved={() => { setAdding(false); load(); }} onDeleted={() => setAdding(false)} />}
        {list?.map((p) => <PackageEditor key={p.id} pkg={p} onSaved={load} onDeleted={load} />)}
      </div>
    </div>
  );
};
